import BottomNavigation from '@/components/BottomNavigation';
import MarketplaceTopNavigation from '@/components/MarketplaceTopNavigation';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card } from '@/components/ui/card';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { FEATURE_FLAGS, isInternationalMarketsUser, isNewMarketplaceUser } from '@/config/features';
import MarketplaceHome from '@/components/marketplace/MarketplaceHome';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import PromoBanner from '@/components/PromoBanner';
import localMarketsImg from '@/assets/category-local-markets.jpg';
import beautyImg from '@/assets/category-beauty.jpg';
import technologyImg from '@/assets/category-technology.webp';
import clothingImg from '@/assets/category-clothing.jpg';
import homeEssentialsImg from '@/assets/category-home-essentials.webp';
import servicesImg from '@/assets/category-services.webp';
import sheinLogo from '@/assets/shein-logo.png';

const categories = [
  { id: 'groceries', name: 'Groceries', image: localMarketsImg },
  { id: 'beauty', name: 'Beauty', image: beautyImg },
  { id: 'technology', name: 'Technology', image: technologyImg },
  { id: 'clothing', name: 'Clothing', image: clothingImg },
  { id: 'home_essentials', name: 'Home Essentials', image: homeEssentialsImg },
  { id: 'services', name: 'Services', image: servicesImg },
];

const GroceryPage = () => {
  const { t } = useLanguage();
  const { isAdmin, loading } = useIsAdmin();
  const { user } = useAuth();
  const navigate = useNavigate();

  const showMarketplace = FEATURE_FLAGS.MARKETPLACE_ENABLED || isAdmin;
  const showInternational = isInternationalMarketsUser(user?.email);
  const useNewMarketplace = isNewMarketplaceUser(user?.email);

  if (loading) {
    return (
      <div className="min-h-screen bg-background pb-20">
        <MarketplaceTopNavigation />
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading...</p>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      <MarketplaceTopNavigation />

      <div className="px-4 py-4">
        {showMarketplace ? (
          useNewMarketplace ? (
            <MarketplaceHome showInternational={showInternational} />
          ) : (
          <>
            <h1 className="text-2xl font-bold mb-6 text-foreground">Marketplace</h1>
            <PromoBanner type="marketplace" />

            {showInternational && (
              <div className="mt-4 mb-6">
                <h2 className="text-base font-semibold text-foreground mb-3">International Markets</h2>
                <div className="flex gap-4 overflow-x-auto pb-1">
                  <button
                    className="flex flex-col items-center gap-2 flex-shrink-0"
                    onClick={() => {
                      // Open SHEIN externally. In the Median.co mobile app this is
                      // intercepted and opened natively; in the web preview it
                      // opens in the user's preferred browser as a new tab.
                      window.open('https://shein.com', '_blank', 'noopener,noreferrer');
                    }}
                  >
                    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-border bg-white flex items-center justify-center">
                      <img
                        src={sheinLogo}
                        alt="SHEIN"
                        loading="lazy"
                        width={80}
                        height={80}
                        className="w-14 h-14 object-contain"
                      />
                    </div>
                    <span className="text-xs text-foreground font-medium">SHEIN</span>
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {categories.map((category) => (
                <Card
                  key={category.id}
                  className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => navigate(`/marketplace/${category.id}`)}
                >
                  <div className="aspect-square relative">
                    <img src={category.image} alt={category.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                      <h3 className="text-white font-semibold text-lg p-4 w-full">{category.name}</h3>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </>
          )
        ) : (
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold mb-4">{t('grocery_delivery')}</h1>
            <p className="text-muted-foreground mb-6">{t('fresh_groceries_delivered')}</p>
            <p className="text-sm text-muted-foreground">{t('coming_soon_gambgo')}</p>
          </div>
        )}
      </div>

      <BottomNavigation />
    </div>
  );
};

export default GroceryPage;
